// kotRealtime.js
//
// Thin SignalR wrapper, written so the Captain Dashboard can adopt it as-is
// later. It owns the connection lifecycle and nothing else — it never touches
// data. It tells the caller two things:
//   onChange(payload)  the server says something changed (a hint, not data)
//   onResync(why)      you may have missed events (first connect, reconnect):
//                      re-fetch EVERYTHING
// and the caller decides how to fetch.
//
// Rules this encodes (each one a real failure mode):
//   - the kotChanged handler is registered BEFORE start(), so nothing between
//     start and subscribe is lost;
//   - the reconnect policy never gives up (the default stops after ~4 tries and
//     strands a tablet that slept);
//   - if the very first start() fails, or the connection closes for good, it
//     keeps trying rather than staying dead.

import * as signalR from '@microsoft/signalr';

const RETRY_MS = [0, 2000, 5000, 10000];
const nextDelay = (ctx) => (
    RETRY_MS[ctx.previousRetryCount] !== undefined ? RETRY_MS[ctx.previousRetryCount] : 30000
);
const RESTART_MS = 5000;

export function startKotRealtime({ url, onChange, onResync, onStatus, onLog }) {
    let stopped = false;
    let conn = null;
    let timer = null;

    const log = (m) => { if (onLog) onLog(m); };

    const connect = async () => {
        if (stopped) return;

        const c = new signalR.HubConnectionBuilder()
            .withUrl(url)
            .withAutomaticReconnect({ nextRetryDelayInMilliseconds: nextDelay })
            .build();
        conn = c;

        c.on('kotChanged', (p) => onChange(p));
        c.onreconnecting((e) => {
            onStatus('reconnecting');
            log('reconnecting: ' + (e && e.message));
        });
        c.onreconnected(() => {
            onStatus('connected');
            onResync('reconnected');
        });
        c.onclose((e) => {
            onStatus('offline');
            if (stopped) return;
            log('closed' + (e ? ': ' + e.message : '') + ' -> restarting in ' + RESTART_MS / 1000 + 's');
            timer = setTimeout(connect, RESTART_MS);
        });

        try {
            await c.start();
        } catch (e) {
            onStatus('offline');
            log('start failed: ' + e.message + ' -> retrying in ' + RESTART_MS / 1000 + 's');
            if (!stopped) timer = setTimeout(connect, RESTART_MS);
            return;
        }
        if (stopped) { c.stop(); return; }
        onStatus('connected');
        onResync('connected');
    };

    connect();

    return {
        stop() {
            stopped = true;
            clearTimeout(timer);
            if (conn) conn.stop();
        },
    };
}
